import * as React from 'react';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '../utils/propStyles';
import DisclaimerListItem, { Disclaimer } from './DisclaimerListItem';
import { SuperscriptRefHelper } from '../utils/SuperscriptRefHelper';
import { AccessibilityStore } from '../stores';

const DisclaimersList = styled<any, any>('ol')`
    ${propStyles};
`;

DisclaimersList.defaultProps = {
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'normal',
    lineHeight: '1.4',
    color: 'body',
    listStyle: 'none',
};

interface DisclaimersProps {
    accessibilityStore: AccessibilityStore;
    disclaimers: Array<Disclaimer>;
    marginX?: string | number;
    superscriptContext?: string;
    theme?: { [property: string]: string | number };
}

@inject('accessibilityStore')
@observer
class Disclaimers extends React.Component<DisclaimersProps, any> {
    @observable
    focusUpdateTime = -1;
    lastFocusUpdateTime = -1;

    focusedIndex = -1;

    componentDidMount() {
        this.init();
    }

    componentDidUpdate() {
        this.init();
    }

    init() {
        this.disclaimers.forEach((disclaimer, index) => {
            const footnoteId =
                (typeof disclaimer !== 'string' && disclaimer.id) || undefined;
            const composedFootnoteId = `${footnoteId}${this.superscriptContext}`;
            SuperscriptRefHelper.registerFootnote(composedFootnoteId, () => {
                this.focusedIndex = index;
                this.focusUpdateTime = new Date().getTime();
            });
        });
    }

    @computed
    get accessibilityStore() {
        const { accessibilityStore } = this.props;
        return accessibilityStore;
    }

    @computed
    get superscriptContext() {
        return this.props.superscriptContext
            ? `-${this.props.superscriptContext}`
            : '';
    }

    @computed
    get shouldFocus() {
        return this.focusUpdateTime > this.lastFocusUpdateTime;
    }

    @computed
    get disclaimers() {
        return this.props.disclaimers || [];
    }

    @computed
    get hasDisclaimers() {
        return this.disclaimers.length > 0;
    }

    @computed
    get disclaimerList() {
        const { theme, superscriptContext } = this.props;
        return this.disclaimers.map((disclaimer, index) => {
            let isFocused = false;
            if (this.shouldFocus && this.focusedIndex === index) {
                isFocused = true;
                this.lastFocusUpdateTime = this.focusUpdateTime;
            }
            return (
                <DisclaimerListItem
                    key={`disclaimer-item-${index}`}
                    disclaimer={disclaimer}
                    focus={isFocused}
                    focusTime={isFocused ? this.focusUpdateTime : -1}
                    superscriptContext={superscriptContext}
                    theme={theme}
                />
            );
        });
    }

    render() {
        const { theme, ...props } = this.props;

        if (!this.hasDisclaimers) {
            return null;
        }

        const ariaLabel =
            this.accessibilityStore &&
            this.accessibilityStore.superscriptAccessibilityValue(
                'footnoteGroupDescription',
                'Footnote disclaimers'
            );

        return (
            <DisclaimersList
                aria-label={ariaLabel}
                {...props}
                {...theme.DisclaimersList}
            >
                {this.disclaimerList}
            </DisclaimersList>
        );
    }
}

export default withTheme(Disclaimers);



// WEBPACK FOOTER //
// ./src/Components/Disclaimers.tsx